import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import {
  LayoutDashboard, Users2, UserCog, ScrollText, PlayCircle, Mail, FileBadge, BarChart3,
  BookOpen, Shuffle, ListChecks, Sigma, Sparkles, Combine, Target, ShieldCheck, MessageSquareText, Languages,
  Layers, Brain, FileDown, Loader2,
} from "lucide-react";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "nav-admin-dashboard" },
  { to: "/admin/executives", label: "Marketing Executives", icon: UserCog, testId: "nav-admin-executives" },
  { to: "/admin/students", label: "Students", icon: Users2, testId: "nav-admin-students" },
  { to: "/admin/inbox", label: "Inbox", icon: Mail, testId: "nav-admin-inbox" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testId: "nav-admin-audit" },
  { to: "/admin/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-admin-sample-report" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "nav-admin-analytics" },
  { to: "/admin/how-it-works", label: "How It Works", icon: BookOpen, testId: "nav-admin-how-it-works" },
  { to: "/admin/questions", label: "Question Bank", icon: Layers, testId: "nav-admin-questions" },
  { to: "/me/demo", label: "Demo Mode", icon: PlayCircle, testId: "nav-admin-demo" },
];

function StatTile({ value, label }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-center" data-testid={`methodology-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <p className="text-2xl font-bold font-mono text-indigo-700">{value}</p>
      <p className="text-[11px] text-slate-500 mt-1 leading-snug">{label}</p>
    </div>
  );
}

function Step({ icon: Icon, num, title, value, children }) {
  return (
    <AccordionItem value={value} className="border border-slate-200 rounded-xl mb-3 px-5 bg-white" data-testid={`methodology-step-${value}`}>
      <AccordionTrigger className="hover:no-underline">
        <div className="flex items-center gap-3 text-left">
          <div className="h-9 w-9 shrink-0 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 font-mono text-xs font-bold">{num}</div>
          <Icon className="h-4 w-4 text-indigo-500 shrink-0" />
          <span className="font-heading text-sm font-semibold text-slate-800">{title}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="text-sm text-slate-600 leading-relaxed pl-12 space-y-2">
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}

export default function AdminHowItWorks() {
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/admin/methodology/stats").then((res) => setStats(res.data)).catch(() => setStats(null));
  }, []);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/how-it-works/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "careerstudy360_how_it_works.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export the methodology PDF");
    } finally {
      setExporting(false);
    }
  };

  return (
    <DashboardShell links={LINKS} activePath="/admin/how-it-works">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="how-it-works-heading">How CareerStudy360 Works</h1>
        <Button variant="outline" size="sm" data-testid="export-how-it-works-pdf-button" disabled={exporting} onClick={handleExportPdf}>
          {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Export PDF
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-8 max-w-3xl">
        A step-by-step, engineering-accurate walkthrough of the WCPPA assessment engine — from how a unique
        test form is generated for every student, through deterministic + AI-assisted scoring, to how the
        final career ranking is produced. Internal reference for Super Admin.
      </p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-10" data-testid="methodology-stats-grid">
          <StatTile value={stats.total_dimensions} label="Scored Dimensions" />
          <StatTile value={stats.total_slots} label="Item Slots" />
          <StatTile value={stats.total_alternates} label="Unique Item Phrasings" />
          <StatTile value={stats.total_occupations} label="Occupations Ranked" />
          <StatTile value={stats.total_career_families} label="Career Families" />
          <StatTile value={stats.students_assessed} label="Students Assessed" />
        </div>
      )}

      <Accordion type="multiple" defaultValue={["step1"]} data-testid="how-it-works-accordion">
        <Step icon={Shuffle} num="1" title="Unique Test Form Per Student (Item-Exposure Balancing)" value="step1">
          <p>
            The item bank is organised as <strong>{stats?.total_slots ?? "252"} "slots"</strong>, each representing one
            underlying construct/question. Every slot has exactly <strong>4 near-duplicate alternates (A/B/C/D)</strong> —
            differently-worded versions of the exact same question, never shown together to one student.
          </p>
          <p>
            When a student starts their assessment, <code>build_production_form(session_seed)</code> uses a seed
            derived from their session to deterministically: (1) shuffle the order of all slots, and (2) pick ONE
            of the 4 alternates per slot at random. The result is a <strong>frozen, unique test form</strong> for that
            student — out of <strong>{stats?.total_alternates ?? "1,008"} possible phrasings</strong>, no two students
            are statistically likely to see an identical form in the same order. This prevents rote memorization and
            answer-sharing between students, while every alternate still measures the exact same construct so scores
            stay comparable across students.
          </p>
        </Step>

        <Step icon={ListChecks} num="2" title="Four Question Types" value="step2">
          <ul className="list-disc pl-4 space-y-1">
            <li><strong>Likert (self-report, 5-point scale)</strong> — the majority of items. Measures personality, RIASEC interests, career values and professional strengths via agreement statements. Some are "reverse-scored" (agreeing = LOW on the trait) to catch straight-line responding.</li>
            <li><strong>MCQ (cognitive aptitude)</strong> — numerical, verbal, abstract/logical, spatial, mechanical/systems reasoning and data-accuracy puzzles with one objectively correct answer.</li>
            <li><strong>SJT (Situational Judgment Test)</strong> — realistic workplace/school scenarios for leadership, teamwork, conflict management, ethics, initiative and entrepreneurial judgment. Each has an expert-authored "provisional" best-response key, later refined with AI scoring (Step 4).</li>
            <li><strong>Validity / response-quality checks</strong> — attention checks, "infrequency" (implausibly perfect self-descriptions) and consistency-pair items. These are NOT scored into the 40 dimensions — they only feed the Response Validity confidence flag (Step 8).</li>
          </ul>
        </Step>

        <Step icon={Sigma} num="3" title="Deterministic Dimension Scoring" value="step3">
          <p>Every answered item is normalized to a 0-1 scale and added to its dimension's bucket:</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>Likert: <code>(value - 1) / 4</code>, flipped via <code>1 - norm</code> if the item is reverse-scored.</li>
            <li>MCQ: <code>1.0</code> if the chosen option matches <code>correct_id</code>, else <code>0.0</code>.</li>
            <li>SJT: <code>1.0</code> if the response matches the provisional expert key, else a partial <code>0.35</code> (never zero — SJT judgment is rarely binary-wrong).</li>
          </ul>
          <p>
            Each of the <strong>{stats?.total_dimensions ?? 40} dimensions</strong> then gets a final score = the average
            of all its item scores × 100, rounded to 1 decimal. A dimension with no answered items is left as
            <code>null</code> rather than assumed.
          </p>
        </Step>

        <Step icon={MessageSquareText} num="4" title="AI-Assisted SJT Re-Scoring (Claude Sonnet 4.6)" value="step4">
          <p>
            SJT items are judgment calls, not pure right/wrong — so their deterministic score above is only a
            baseline. Every SJT response is separately sent to <strong>Claude Sonnet 4.6</strong>, which rates how
            effective the student's chosen response would be in that real scenario (0-100).
          </p>
          <p>
            The final SJT-linked dimension score blends <strong>40% deterministic baseline + 60% AI effectiveness
            rating</strong>. This blended result is explicitly disclosed to students in the report's SJT Annex as an
            AI + Subject-Matter-Expert interpretation, pending full SME sign-off on the underlying keys.
          </p>
        </Step>

        <Step icon={Sparkles} num="5" title="Interest & Hobby Signal (Small Supplementary Nudge)" value="step5">
          <p>
            At onboarding, students self-select hobbies/interests (e.g. "Coding & Technology", "Sports & Fitness").
            Each tag maps to a small weight on 1-2 related dimensions (e.g. Coding → Investigative +1.0, Realistic +0.4).
          </p>
          <p>
            This can only shift a dimension score by a <strong>maximum of ±5 points out of 100</strong> — deliberately
            capped so hobbies add colour to the profile without ever overriding what the actual assessment responses show.
          </p>
        </Step>

        <Step icon={Combine} num="6" title="Higher-Order Indexes & Broad Domain Fit" value="step6">
          <p>
            The {stats?.total_dimensions ?? 40} raw dimensions are combined into <strong>{stats?.higher_order_indexes_count ?? 12}
            higher-order potential indexes</strong> (e.g. Analytical Potential, Leadership Potential, Entrepreneurial
            Potential) — each a simple average of a hand-picked, psychologically-grounded subset of dimensions.
          </p>
          <p>
            Separately, <strong>{stats?.broad_domain_fit_count ?? 4} Broad Domain Fit</strong> scores (Engineering &
            Technology, Medical & Healthcare, Chartered Accountancy & Finance, Creative Arts & Design) give a quick
            directional read before the detailed career-match ranking below.
          </p>
        </Step>

        <Step icon={Target} num="7" title="Career Matching & Ranking Algorithm" value="step7">
          <p>
            Each of the <strong>{stats?.total_occupations ?? "800+"} O*NET-mapped occupations</strong> (grouped into{" "}
            {stats?.total_career_families ?? "45+"} career families) is pre-defined as a sparse "vector" — its expected
            profile across a handful of the 40 dimensions most relevant to that job.
          </p>
          <p>For every occupation, the student's scores are compared against that vector using a hybrid alignment score:</p>
          <ul className="list-disc pl-4 space-y-1">
            <li><strong>60% Shape Fit</strong> — a Pearson-style correlation between the student's normalized scores and the occupation's expected profile, rescaled from -1..1 to 0..1.</li>
            <li><strong>40% Magnitude Fit</strong> — a penalty term (<code>1 - average absolute gap</code>) so a uniformly LOW-scoring student can no longer score near-perfect against a uniformly HIGH-demand occupation just because the "shape" correlates — pure correlation alone can't tell those apart.</li>
          </ul>
          <p>
            All occupations are ranked by this <strong>Overall Alignment %</strong>, and the <strong>top 5</strong> are
            returned as the student's Career Matches. Each match also gets 6 axis-level sub-scores (Interest, Aptitude,
            Personality, Values, Work Environment, Professional Behaviour alignment) using a lighter-weight version of
            the same formula, for the radar-style breakdown shown on the report.
          </p>
        </Step>

        <Step icon={ShieldCheck} num="8" title="Response Validity & Confidence Level" value="step8">
          <p>
            The Validity-type items (Step 2) are aggregated into a flag count: an extreme "infrequency" average,
            a failed attention check, or a consistency-pair mismatch each add 1 flag.
          </p>
          <p>
            <strong>0 flags → High confidence</strong>, <strong>1 flag → Moderate</strong>, <strong>2+ flags → Caution</strong>.
            This level is shown to the student in plain language and tracked in aggregate on the Admin Analytics
            "Assessment Confidence Trends" panel so low-validity submissions can be reviewed with the student directly.
          </p>
        </Step>

        <Step icon={MessageSquareText} num="9" title="AI Narrative Generation (Claude Sonnet 4.6)" value="step9">
          <p>
            All numbers above are computed deterministically first and handed to Claude Sonnet 4.6 as fixed input —
            the AI is instructed it can <strong>never invent, alter or recalculate any score or ranking</strong>. Its
            only job is to write the warm, plain-language interpretation text (snapshot, personality/interest/aptitude
            profiles, strengths, development priorities, career-match rationale, closing summary).
          </p>
          <p>
            A strict language-calibration rule keeps wording proportionate to the actual number (e.g. a score must be
            85+ before the AI is allowed to call it "exceptional") so no dimension is talked up beyond what the data supports.
          </p>
        </Step>

        <Step icon={Languages} num="10" title="Hindi Translation Layer" value="step10">
          <p>
            Once a report is generated in English, a student can toggle to Hindi at any time. The full report
            (narrative, archetypes, career matches, SJT Annex) is translated on-demand by Claude Sonnet 4.6 and then
            cached per report, so repeated views don't re-translate or drift from the English source.
          </p>
        </Step>

        <Step icon={Brain} num="11" title="The Aptitude Test Engine — 8-Part Performance-Based Module" value="step11">
          <p>
            A separate, optional Aptitude Test complements the 252-item self-report assessment with 8 timed,
            performance-based exercises — inspired by aptitude batteries like YouScience — instead of Likert
            self-report: Numerical Reasoning, Vocabulary, Inductive Reasoning, Spatial Visualization, Sequential
            Reasoning, Visual Comparison Speed, Idea Generation and Work Approach Style.
          </p>
          <p>
            6 subtests are right/wrong or speed-scored (0-100) and, once complete, are <strong>blended into the
            matching WCPPA Cognitive & Professional Aptitude dimension</strong> at a 60% existing-score / 40%
            Aptitude-Test-score weighting — the career-match ranking is then recomputed on the blended scores,
            regardless of which module the student completes first.
          </p>
          <p>
            Idea Generation is scored by counting distinct ideas (fluency) blended 50/50 with a Claude Sonnet 4.6
            originality rating. Work Approach Style produces a 4-axis preference profile (Structured/Adaptive,
            Independent/Collaborative, Detail/Big-Picture, Cautious/Bold) — not right/wrong, purely descriptive.
            Both are woven into the main report's AI narrative as supplementary qualitative context, reinforcing
            without ever overriding the core validated WCPPA instrument.
          </p>
        </Step>
      </Accordion>
    </DashboardShell>
  );
}
